import 'keen-slider/keen-slider.min.css';
import designTokens from '@customink/design-tokens/build/ci_light/js/designTokens';
import {
  ArrowDownIcon,
  Box,
  IconButton,
  Typography
} from '@customink/pigment-react';
import {styled} from '@mui/material/styles';
import useResizeObserver from '@react-hook/resize-observer';
import KeenSlider from 'keen-slider';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import useBreakpoint from 'hooks/useBreakpoint';
import useIntersectionObserverOnce from 'hooks/useIntersectionObserverOnce';
import RelatedProductCard from 'product_details_page/RelatedProducts/RelatedProductCard';
import {
  EVENT_TYPE,
  fireSeptemberPromoEvent
} from 'utils/metrics/septemberPromoBanner';
import {CarouselSkeleton} from './components/LoadingSkeleton';
import useTrendingProducts from './hooks/useTrendingProducts';

const Container = styled(Box)(() => ({
  marginBottom: '2.5rem',
  marginTop: '2.5rem',
  width: '100%'
}));

const TrendingProductsHeader = styled(Box)(() => ({
  marginBottom: '1.5rem',
  textAlign: 'left'
}));

const CarouselContainer = styled('div')(() => ({
  display: 'flex',
  height: 'max-content',
  position: 'relative',
  width: '100%'
}));

const SliderWrapper = styled('div')(() => ({
  '.arrow-container': {
    '.arrow': {
      '&.disabled': {
        backgroundColor: 'transparent',
        boxShadow: 'none',
        cursor: 'default'
      },
      '&.left svg': {transform: 'rotate(90deg) !important;'},
      '&.right svg': {transform: 'rotate(-90deg);'},
      '&:active': {
        backgroundColor: designTokens.color.gray.active.original.value
      },
      '&:hover': {
        backgroundColor: '#f3f3f3'
      },
      backgroundColor: '#fff',
      boxShadow: '0 0.25rem 0.5rem rgba(0, 0, 0, 0.08)',
      fontSize: '1.25rem',
      height: '2rem',
      margin: '0 0.5rem',
      pointerEvents: 'auto',
      width: '2rem',
      zIndex: 1
    },
    alignItems: 'center',
    bottom: 0,
    display: 'flex',
    justifyContent: 'space-between',
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0
  },
  '.keen-slider': {
    display: 'flex !important',
    height: '100%',
    overflow: 'hidden !important',
    position: 'relative',
    touchAction: 'pan-y',
    userSelect: 'none',
    WebkitTouchCallout: 'none'
  },
  '.keen-slider__slide': {
    minHeight: '100%',
    overflow: 'hidden',
    position: 'relative'
  },
  backgroundColor: 'transparent',
  height: '100%',
  overflow: 'hidden',
  position: 'relative',
  width: '100%'
}));

const TrendingProductsCarousel = () => {
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === 'sm';
  const {isLoading, products} = useTrendingProducts();

  const [isFirstSlide, setIsFirstSlide] = useState(true);
  const [isLastSlide, setIsLastSlide] = useState(false);
  const rootRef = useRef(null);

  const slidesPerView = isMobile ? 1.6 : 4;
  const gap = isMobile ? 8 : 16;

  const sliderRef = useRef(null);
  const instanceRef = useRef(null);

  useIntersectionObserverOnce(rootRef, () => {
    fireSeptemberPromoEvent({
      eventType: EVENT_TYPE.MODULE_VIEW
    });
  });

  useEffect(() => {
    if (sliderRef.current && products.length > 0) {
      instanceRef.current = new KeenSlider(sliderRef.current, {
        created: () => {
          setIsFirstSlide(true);
          setIsLastSlide(false);
        },
        initial: 0,
        loop: true,
        mode: 'snap',
        slideChanged: () => {
          setIsFirstSlide(false);
          setIsLastSlide(false);
        },
        slidesPerView,
        spacing: gap
      });
    }

    return () => {
      if (instanceRef.current) {
        instanceRef.current.destroy();
        instanceRef.current = null;
      }
    };
  }, [products.length, slidesPerView, gap, isMobile]);

  const boundingBoxRef = useRef(null);
  useResizeObserver(boundingBoxRef, () => instanceRef?.current?.refresh());

  const handleLeftClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (instanceRef?.current?.prev) {
        instanceRef.current.prev();
        fireSeptemberPromoEvent({
          eventType: EVENT_TYPE.CAROUSEL_PREV
        });
      }
    },
    [fireSeptemberPromoEvent]
  );

  const handleRightClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (instanceRef?.current?.next) {
        instanceRef.current.next();
        fireSeptemberPromoEvent({
          eventType: EVENT_TYPE.CAROUSEL_NEXT
        });
      }
    },
    [fireSeptemberPromoEvent]
  );

  if (isLoading) {
    return <CarouselSkeleton />;
  }

  if (products.length === 0) {
    return null;
  }

  const showArrows = products.length >= 2 && !isMobile;

  const productCards = products.map((product) => (
    <RelatedProductCard
      key={`trending-carousel-${product.style_id}`}
      sx={{
        width: '100%',
        ...(isMobile && {
          '& .MuiCardActionArea-root': {
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
          },
          '& .MuiCardActionArea-root > div:first-of-type': {
            flexShrink: 0,
            height: '13rem !important',
            paddingTop: '0 !important',
            width: '100% !important'
          },
          '& .MuiCardContent-root': {
            display: 'flex',
            flex: '1 1 auto',
            flexDirection: 'column',
            gap: '0.25rem !important',
            minHeight: 'calc(100% - 13rem) !important',
            padding: '0.5rem !important'
          },
          '& [data-testid="reco-product-name"]': {
            marginBottom: '0 !important',
            minHeight: '2rem',
            WebkitLineClamp: 2
          },
          height: '22rem'
        })
      }}
      onClick={() => {
        fireSeptemberPromoEvent({
          eventType: EVENT_TYPE.ITEM_CLICKED,
          styleId: product.style_id
        });
        window.location.href = product.link;
      }}
      imageSrc={product.image_src}
      imageSrcSet={product.image_srcset || ''}
      minQty={product.min_qty}
      name={product.name}
      ratingCount={product.rating_count}
      ratingScore={product.rating_score}
      sizing={product.sizing}
      sizes={product.sizes}
      generalComments={product.general_comments || ''}
      defaultUnitPrices={product.default_unit_prices}
      primaryCategoryId={product.primary_category_id}
      styleType={product.style_type}
      defaultQuoteQty={product.default_quote_qty}
      productId={product.style_id}
      styleId={product.style_id}
      showDiscount
    />
  ));

  return (
    <Container ref={rootRef}>
      <TrendingProductsHeader>
        <Typography
          sx={{
            color: 'rgba(0, 0, 0, 0.86)',
            fontSize: '1.5rem',
            fontWeight: 700,
            lineHeight: 1.25
          }}>
          Trending Products
        </Typography>
      </TrendingProductsHeader>
      <CarouselContainer>
        <SliderWrapper ref={boundingBoxRef}>
          {showArrows && (
            <div className="arrow-container">
              <IconButton
                disableRipple
                className={`arrow left ${isFirstSlide ? 'disabled' : ''}`}
                onClick={handleLeftClick}
                disabled={isFirstSlide}>
                <ArrowDownIcon
                  sx={{fill: designTokens.color.gray.slateDefault.value}}
                  fontSize="inherit"
                />
              </IconButton>

              <IconButton
                disableRipple
                className={`arrow right ${isLastSlide ? 'disabled' : ''}`}
                onClick={handleRightClick}
                disabled={isLastSlide}>
                <ArrowDownIcon
                  sx={{fill: designTokens.color.gray.slateDefault.value}}
                  fontSize="inherit"
                />
              </IconButton>
            </div>
          )}
          <div
            ref={sliderRef}
            className="keen-slider"
            style={{paddingRight: '0'}}>
            {productCards.map((slide, i) => (
              <div key={slide.key} className={`keen-slider__slide slide-${i}`}>
                {slide}
              </div>
            ))}
          </div>
        </SliderWrapper>
      </CarouselContainer>
    </Container>
  );
};

export default TrendingProductsCarousel;
